import { BILLING_CYCLE_LABELS, SUBSCRIPTION_STATUS_LABELS, type MockCheckoutSessionDto, type MockPortalActionInput } from "@iagenix/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, redirect } from "@tanstack/react-router";
import { AlertTriangle, CreditCard, FileText, FlaskConical } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Logo } from "@/components/logo";
import { ErrorState, LoadingScreen } from "@/components/states";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ApiRequestError, errorMessage } from "@/lib/api";
import { billingApi, billingQueries, brl } from "@/lib/billing-api";
import { queries } from "@/lib/queries";

/**
 * Fase 9 — Checkout e Portal simulados (BILLING_MODE=mock). Cada botão monta os eventos no formato
 * do Stripe, assina com o segredo do webhook e envia para POST /api/billing/stripe/webhook.
 */
export const Route = createFileRoute("/cobranca/simulado/$sessionId")({
  beforeLoad: async ({ context, location }) => {
    try {
      await context.queryClient.query({ ...queries.me(), staleTime: 60_000 });
    } catch (err) {
      if (err instanceof ApiRequestError && err.status === 401) {
        throw redirect({ to: "/entrar", search: { redirecionar: location.href } });
      }
      throw err;
    }
  },
  component: MockBillingPage,
});

function MockBillingPage() {
  const { sessionId } = Route.useParams();
  const session = useQuery(billingQueries.mockSession(sessionId));
  return (
    <div className="flex min-h-screen flex-col bg-muted/40">
      <header className="flex items-center justify-between border-b bg-background px-6 py-3">
        <Logo />
        <Badge variant="warning">
          <FlaskConical className="mr-1 size-3" aria-hidden />
          Stripe simulado
        </Badge>
      </header>
      <main className="mx-auto w-full max-w-2xl flex-1 p-6">
        {session.isPending ? (
          <LoadingScreen label="Abrindo a sessão…" />
        ) : session.isError ? (
          <ErrorState error={session.error} onRetry={() => void session.refetch()} />
        ) : session.data.kind === "checkout" ? (
          <Checkout session={session.data} />
        ) : (
          <Portal session={session.data} />
        )}
      </main>
    </div>
  );
}

function SimulatedNotice() {
  return (
    <Alert>
      <FlaskConical className="size-4" />
      <AlertTitle>Ambiente de teste</AlertTitle>
      <AlertDescription>
        Esta página substitui o Stripe enquanto BILLING_MODE=mock. Nada é cobrado: cada botão envia ao sistema os mesmos webhooks
        assinados que o Stripe enviaria.
      </AlertDescription>
    </Alert>
  );
}

function Checkout({ session }: { session: MockCheckoutSessionDto }) {
  const queryClient = useQueryClient();
  const [done, setDone] = useState<MockCheckoutSessionDto | null>(null);
  const pay = useMutation({
    mutationFn: (outcome: "cartao" | "boleto" | "recusado") => billingApi.completeMock(session.id, outcome),
    onSuccess: async (r) => {
      setDone(r);
      await queryClient.invalidateQueries({ queryKey: ["workspaces", session.workspaceId] });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const result = done ?? (session.status !== "aberta" ? session : null);
  return (
    <div className="space-y-6">
      <SimulatedNotice />
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">
            Assinar {session.plan?.name} · {BILLING_CYCLE_LABELS[session.cycle]}
          </CardTitle>
          <CardDescription>Workspace {session.workspaceName}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>
            <span className="text-3xl font-semibold">{brl(session.amountCents)}</span>
            <span className="text-muted-foreground">/{session.cycle === "anual" ? "ano" : "mês"}</span>
          </p>
          {session.coupon ? (
            <p className="text-sm text-emerald-700 dark:text-emerald-400">
              Cupom {session.coupon.code} aplicado ({session.coupon.percentOff ? `${session.coupon.percentOff}%` : brl(session.coupon.amountOffCents)}).
            </p>
          ) : null}
          {result ? (
            <Alert>
              <AlertTitle>{result.subscriptionStatus === "active" ? "Pagamento aprovado" : result.status === "expirada" ? "Sessão expirada" : "Boleto gerado"}</AlertTitle>
              <AlertDescription>
                {result.subscriptionStatus === "active"
                  ? `Assinatura ${result.plans.find((p) => p.code === result.currentPlanCode)?.name ?? ""} ativa. Os novos limites já valem.`
                  : result.status === "expirada"
                    ? "Volte e comece de novo."
                    : "O boleto vence em 3 dias. Quando for pago (simule em Gerenciar assinatura → Pagar cobrança pendente), a assinatura é ativada."}
              </AlertDescription>
            </Alert>
          ) : (
            <div className="grid gap-2 sm:grid-cols-3">
              <Button onClick={() => pay.mutate("cartao")} loading={pay.isPending && pay.variables === "cartao"}>
                <CreditCard />
                Pagar com cartão
              </Button>
              <Button variant="outline" onClick={() => pay.mutate("boleto")} loading={pay.isPending && pay.variables === "boleto"}>
                <FileText />
                Gerar boleto
              </Button>
              <Button variant="outline" onClick={() => pay.mutate("recusado")} loading={pay.isPending && pay.variables === "recusado"}>
                <AlertTriangle />
                Cartão recusado
              </Button>
            </div>
          )}
          <Button asChild variant={result ? "default" : "ghost"}>
            <Link to="/app/$workspaceId/cobranca" params={{ workspaceId: session.workspaceId }}>
              {result ? "Voltar ao IAGENIX Ads" : "Cancelar e voltar"}
            </Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function Portal({ session }: { session: MockCheckoutSessionDto }) {
  const queryClient = useQueryClient();
  const [current, setCurrent] = useState(session);
  const run = useMutation({
    mutationFn: (input: MockPortalActionInput) => billingApi.portalMock(session.id, input),
    onSuccess: async (r, input) => {
      setCurrent(r);
      await queryClient.invalidateQueries({ queryKey: ["workspaces", session.workspaceId] });
      toast.success(
        input.action === "simular_falha"
          ? "Falha de pagamento enviada (invoice.payment_failed)."
          : input.action === "avancar_tolerancia"
            ? "Tolerância vencida: o workspace entrou em modo leitura."
            : "Feito. O webhook foi processado.",
      );
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const busy = (action: string) => run.isPending && run.variables?.action === action;
  const status = current.subscriptionStatus;
  return (
    <div className="space-y-6">
      <SimulatedNotice />
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">Gerenciar assinatura</CardTitle>
          <CardDescription>
            {current.workspaceName} · plano {current.plans.find((p) => p.code === current.currentPlanCode)?.name ?? current.currentPlanCode} ·{" "}
            {status ? SUBSCRIPTION_STATUS_LABELS[status] : "—"}
            {current.cancelAtPeriodEnd ? " · cancela no fim do período" : ""}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <section className="space-y-2">
            <h2 className="text-sm font-medium">Trocar de plano</h2>
            <div className="flex flex-wrap gap-2">
              {current.plans
                .filter((p) => p.code !== current.currentPlanCode)
                .map((p) => (
                  <Button key={p.code} variant="outline" size="sm" loading={busy("trocar_plano") && run.variables?.planCode === p.code} onClick={() => run.mutate({ action: "trocar_plano", planCode: p.code })}>
                    {p.name} · {brl(p.priceMonthlyCents)}/mês
                  </Button>
                ))}
            </div>
          </section>
          <section className="space-y-2">
            <h2 className="text-sm font-medium">Pagamento</h2>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" loading={busy("trocar_cartao")} onClick={() => run.mutate({ action: "trocar_cartao" })}>
                Atualizar cartão
              </Button>
              <Button size="sm" loading={busy("pagar_pendente")} onClick={() => run.mutate({ action: "pagar_pendente" })}>
                Pagar cobrança pendente
              </Button>
            </div>
          </section>
          <section className="space-y-2">
            <h2 className="text-sm font-medium">Cancelamento</h2>
            <div className="flex flex-wrap gap-2">
              {current.cancelAtPeriodEnd ? (
                <Button variant="outline" size="sm" loading={busy("reativar")} onClick={() => run.mutate({ action: "reativar" })}>
                  Reativar
                </Button>
              ) : (
                <Button variant="outline" size="sm" loading={busy("cancelar")} onClick={() => run.mutate({ action: "cancelar" })}>
                  Cancelar no fim do período
                </Button>
              )}
              <Button variant="destructive" size="sm" loading={busy("cancelar_agora")} onClick={() => run.mutate({ action: "cancelar_agora" })}>
                Cancelar agora
              </Button>
            </div>
          </section>
          <section className="space-y-2 rounded-lg border border-dashed p-3">
            <h2 className="text-sm font-medium">Simulações de teste</h2>
            <p className="text-xs text-muted-foreground">Não existem no Stripe real: servem para ensaiar falhas e o bloqueio suave.</p>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" loading={busy("simular_falha")} onClick={() => run.mutate({ action: "simular_falha" })}>
                Simular falha de pagamento
              </Button>
              <Button variant="outline" size="sm" loading={busy("avancar_tolerancia")} onClick={() => run.mutate({ action: "avancar_tolerancia" })}>
                Avançar 3 dias (vencer a tolerância)
              </Button>
              <Button variant="outline" size="sm" loading={busy("expirar_teste")} onClick={() => run.mutate({ action: "expirar_teste" })}>
                Expirar o teste grátis
              </Button>
            </div>
          </section>
          <Button asChild>
            <Link to="/app/$workspaceId/cobranca" params={{ workspaceId: session.workspaceId }}>
              Voltar ao IAGENIX Ads
            </Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
